/**
 * EventSource cannot send Authorization headers or use the axios refresh interceptor.
 * Reconnects must read a still-valid access token (refresh when near expiry).
 *
 * Uses raw axios for refresh to avoid the api interceptor clearing tokens on a
 * failed nested refresh (common when multiple SSE streams reconnect together).
 */
import axios from 'axios';
import type { RefreshTokenResponse } from '../types/auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as { exp?: number };
  } catch {
    return null;
  }
}

function getJwtExpMs(token: string): number | null {
  const payload = decodeJwtPayload(token);
  return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
}

let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessTokenRaw(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  const response = await axios.post<{ success?: boolean; data: RefreshTokenResponse }>(
    `${API_BASE_URL}/auth/refresh`,
    { refreshToken },
    { timeout: 15000 }
  );
  const data = response.data?.data;
  if (!data?.accessToken) return null;

  localStorage.setItem('accessToken', data.accessToken);
  if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
  if (data.user) {
    window.dispatchEvent(new CustomEvent('tokenRefreshed', { detail: { user: data.user } }));
  }
  return data.accessToken;
}

/** Returns a usable access token, refreshing when missing/expired/near expiry. */
export async function getSseAccessToken(skewMs = 60_000): Promise<string | null> {
  const token = localStorage.getItem('accessToken');
  const expMs = token ? getJwtExpMs(token) : null;

  // Valid token with known expiry → use it.
  if (token && expMs && expMs > Date.now() + skewMs) return token;

  // Token present but payload unreadable → still try it (server will reject if bad).
  // Only refresh when we know it's expired/near-expiry, or when there is no token.
  const knownExpired = Boolean(token && expMs && expMs <= Date.now() + skewMs);
  const missing = !token;
  if (!knownExpired && !missing && token) return token;

  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const next = await refreshAccessTokenRaw();
      return next || token;
    } catch {
      return token;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export function getAccessTokenTtlMs(token: string | null | undefined): number | null {
  if (!token) return null;
  const expMs = getJwtExpMs(token);
  if (!expMs) return null;
  return expMs - Date.now();
}
